import { PageSEO } from '@/components/common/blog/elements/SEO';
import PageLayout from '../common/page-layout/PageLayout';
import Image, { StaticImageData } from 'next/image';
import { PurchasingOptions } from '../home-page/purchasing-options/PurchasingOptions';
import { SectionDetails } from '@/app/types/marketing/SectionDetails';
import { useRouter } from 'next/router';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';
import { useAuth } from '@/app/context/AuthContext/useAuth';

interface IProps {
  heroImage: StaticImageData;
  analyticsName: string;
  heroMiniTitle: string;
  heroDescription: string;
  heroTitle: string;
  heroImageUrl: string;
  overviewSection: SectionDetails;
  tutorialSection: SectionDetails;
}

export default function FeatureLandingPage(props: IProps) {
  const router = useRouter();

  const { showAuth } = useAuth();

  const { trackEvent, analyticsInstance } = useAnalytics();

  const onLearnMoreclick = () => {
    trackEvent(analyticsInstance, props.analyticsName + '_click_learn_more');
    scrollToOverview();
  };

  const scrollToOverview = () => {
    var element_to_scroll_to = document.getElementById('overview');
    const y =
      element_to_scroll_to.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({
      top: y,
      behavior: 'smooth'
    });
  };

  return (
    <PageLayout>
      <PageSEO
        title={props.heroMiniTitle}
        description={props.heroDescription}
      />
      <div className="bg-gray-900">
        {/* Hero section */}
        <div className="relative isolate overflow-hidden">
          <svg
            aria-hidden="true"
            className="absolute inset-0 -z-10 h-full w-full stroke-white/10 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
          >
            <defs>
              <pattern
                x="50%"
                y={-1}
                id="983e3e4c-de6d-4c3f-8d64-b9761d1534cc"
                width={200}
                height={200}
                patternUnits="userSpaceOnUse"
              >
                <path d="M.5 200V.5H200" fill="none" />
              </pattern>
            </defs>
            <svg x="50%" y={-1} className="overflow-visible fill-gray-800/20">
              <path
                d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
                strokeWidth={0}
              />
            </svg>
            <rect
              fill="url(#983e3e4c-de6d-4c3f-8d64-b9761d1534cc)"
              width="100%"
              height="100%"
              strokeWidth={0}
            />
          </svg>
          <div
            aria-hidden="true"
            className="absolute left-[calc(50%-4rem)] top-10 -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:left-48 lg:top-[calc(50%-30rem)] xl:left-[calc(50%-24rem)]"
          >
            <div
              style={{
                clipPath:
                  'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)'
              }}
              className="aspect-[1108/632] w-[69.25rem] bg-gradient-to-r from-[#80caff] to-[#4f46e5] opacity-20"
            />
          </div>
          <div className="mx-auto max-w-7xl px-6 pb-24 pt-10 sm:pb-40 lg:flex lg:px-8 lg:pt-40">
            <div className="mx-auto max-w-2xl flex-shrink-0 lg:mx-0 lg:max-w-xl lg:pt-8">
              <img
                alt="Your Company"
                src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=500"
                className="invisible h-11"
              />
              <div className="mt-24 sm:mt-32 lg:mt-16">
                <div className="flex">
                  <div className="relative flex items-center gap-x-4 rounded-full bg-indigo-500/10 px-4 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10">
                    <span className="font-semibold text-gray-400">
                      {props.heroMiniTitle}
                    </span>
                    {/* <span
                      aria-hidden="true"
                      className="h-4 w-px bg-gray-900/10"
                    />
                    <div className="flex items-center gap-x-1">
                      AI Study Tools
                    </div> */}
                  </div>
                </div>
              </div>
              {/* <div className="mt-24 sm:mt-32 lg:mt-16">
                <a href="#" className="inline-flex space-x-6">
                  <span className="rounded-full bg-indigo-500/10 px-3 py-1 text-sm font-semibold leading-6 text-indigo-400 ring-1 ring-inset ring-indigo-500/20">
                    Latest updates
                  </span>
                  <span className="inline-flex items-center space-x-2 text-sm font-medium leading-6 text-gray-300">
                    <span>Just shipped v1.0</span>
                    <ChevronRightIcon
                      aria-hidden="true"
                      className="h-5 w-5 text-gray-500"
                    />
                  </span>
                </a>
              </div> */}
              <h1 className="mt-10 text-4xl font-bold tracking-tight text-white sm:text-6xl">
                {props.heroTitle}
              </h1>
              <p className="mt-6 text-lg leading-8 text-gray-300">
                {props.heroDescription}
              </p>
              <div className="mt-10 flex items-center gap-x-6">
                <a
                  onClick={() => {
                    trackEvent(
                      analyticsInstance,
                      props.analyticsName + '_click_get_started'
                    );
                    showAuth(true);
                  }}
                  className="rounded-md bg-primary px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
                >
                  Get started
                </a>
                <a
                  onClick={onLearnMoreclick}
                  className="text-sm font-semibold leading-6 text-white"
                >
                  Learn more <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
            <div className="mx-auto mt-16 flex max-w-2xl sm:mt-24 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
              <div className="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
                <Image
                  alt="App screenshot"
                  src={props.heroImage}
                  className="w-[76rem] rounded-md bg-white/5 shadow-2xl ring-1 ring-white/10"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-18 mb-18 mx-auto px-6 opacity-30 lg:px-8">
          <h2 className="text-center text-lg font-semibold leading-8 text-white">
            Trusted by students for every subject
          </h2>
          <div className="mx-auto mt-8 grid max-w-lg grid-cols-4 items-center gap-x-8 gap-y-12 sm:max-w-xl sm:grid-cols-6 sm:gap-x-10 sm:gap-y-14 lg:mx-0 lg:max-w-none lg:grid-cols-5">
            <Image
              alt="Foreign Language"
              src="/foreign_language_white.png"
              width={158}
              height={48}
              className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
            />
            <Image
              alt="History"
              src="/history_white.png"
              width={158}
              height={48}
              className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
            />
            <Image
              alt="Medicine"
              src="/medicine_white.png"
              width={158}
              height={48}
              className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
            />
            <Image
              alt="Computer Science"
              src="/computer_science_white.png"
              width={158}
              height={48}
              className="col-span-2 max-h-12 w-full object-contain sm:col-start-2 lg:col-span-1"
            />
            <Image
              alt="Biology"
              src="/biology_white.png"
              width={158}
              height={48}
              className="col-span-2 col-start-2 max-h-12 w-full object-contain sm:col-start-auto lg:col-span-1"
            />
          </div>
        </div>
        {/* Feature section */}
        <div id="overview" className="mt-32 sm:mt-56">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl sm:text-center">
              <h2 className="text-base font-semibold leading-7 text-primary">
                {props.overviewSection.miniText}
              </h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                {props.overviewSection.title}
              </p>
              <p className="mt-6 text-lg leading-8 text-gray-300">
                {props.overviewSection.description}
              </p>
            </div>
          </div>
          <div className="relative overflow-hidden pt-16">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <Image
                alt="Overview image"
                src={props.overviewSection.image}
                className="mb-[-12%] rounded-xl shadow-2xl ring-1 ring-white/10"
              />
              <div aria-hidden="true" className="relative">
                <div className="absolute -inset-x-20 bottom-0 bg-gradient-to-t from-gray-900 pt-[7%]" />
              </div>
            </div>
          </div>
          <div className="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 md:mt-24 lg:px-8">
            <dl className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-10 text-base leading-7 text-gray-300 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
              {props.overviewSection.features.map((feature) => (
                <div key={feature.name} className="relative pl-9">
                  <dt className="inline font-semibold text-white">
                    <feature.icon
                      aria-hidden="true"
                      className="absolute left-1 top-1 h-5 w-5 text-primary"
                    />
                    {feature.name}
                  </dt>{' '}
                  <dd className="inline">{feature.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
        <div className="mt-12 overflow-hidden py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
              <div className="lg:pr-8 lg:pt-4">
                <div className="lg:max-w-lg">
                  <h2 className="text-base font-semibold leading-7 text-primary">
                    {props.tutorialSection.miniText}
                  </h2>
                  <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                    {props.tutorialSection.title}
                  </p>
                  <p className="mt-6 text-lg leading-8 text-gray-300">
                    {props.tutorialSection.description}
                  </p>
                  <dl className="mt-10 max-w-xl space-y-8 text-base leading-7 text-gray-300 lg:max-w-none">
                    {props.tutorialSection.features.map((feature) => (
                      <div key={feature.name} className="relative pl-9">
                        <dt className="inline font-semibold text-white">
                          <feature.icon
                            aria-hidden="true"
                            className="absolute left-1 top-1 h-5 w-5 text-primary"
                          />
                          {feature.name}
                        </dt>{' '}
                        <dd className="inline">{feature.description}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
              <Image
                alt="Product screenshot"
                src={props.tutorialSection.image}
                className="w-[48rem] max-w-none rounded-xl shadow-xl ring-1 ring-white/10 sm:w-[70rem] md:-ml-4 lg:-ml-0"
              />
            </div>
          </div>
        </div>
        <section className="bg-gray-50">
          <PurchasingOptions />
        </section>
      </div>
    </PageLayout>
  );
}
